import { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

// Floating orb component for background atmosphere
function FloatingOrb({
  size,
  color,
  initialX,
  initialY,
  duration
}: {
  size: number;
  color: string;
  initialX: number;
  initialY: number;
  duration: number;
}) {
  return (
    <div
      style={{
        position: 'absolute',
        width: size,
        height: size,
        borderRadius: '50%',
        background: color,
        filter: 'blur(60px)',
        opacity: 0.6,
        left: `${initialX}%`,
        top: `${initialY}%`,
        animation: `float-${Math.round(initialX)} ${duration}s ease-in-out infinite`,
        pointerEvents: 'none',
      }}
    />
  );
}

// Email icon SVG
function EmailIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M22 7l-10 5L2 7" />
    </svg>
  );
}

// Lock icon SVG
function LockIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

// Loading spinner
function Spinner() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round" />
    </svg>
  );
}

// Eye icon (password visible)
function EyeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

// Eye off icon (password hidden)
function EyeOffIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

// Map Supabase error messages to user-friendly messages
function getErrorMessage(error: string): string {
  const errorLower = error.toLowerCase();

  if (errorLower.includes('invalid login credentials') || errorLower.includes('invalid credentials')) {
    return 'Incorrect email or password. Please try again.';
  }
  if (errorLower.includes('email not confirmed')) {
    return 'Please verify your email address before signing in.';
  }
  if (errorLower.includes('user not found')) {
    return 'No account found with this email address.';
  }
  if (errorLower.includes('too many requests') || errorLower.includes('rate limit')) {
    return 'Too many login attempts. Please wait a moment.';
  }
  if (errorLower.includes('network') || errorLower.includes('fetch')) {
    return 'Connection error. Please check your internet.';
  }
  if (errorLower.includes('disabled') || errorLower.includes('banned')) {
    return 'This account has been disabled. Contact support.';
  }

  return 'Unable to sign in. Please try again.';
}

// Validate email format
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);

  const { signIn, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Redirect authenticated users to home
  if (authLoading) {
    return null;
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setEmailError('');

    // Validate email before submitting
    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        setError(getErrorMessage(error.message));
      } else {
        void navigate('/shifts');
      }
    } catch {
      setError('Connection error. Please check your internet.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* CSS Keyframes */}
      <style>{`
        @keyframes float-10 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        @keyframes float-70 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-40px, 30px) scale(1.15); }
          66% { transform: translate(25px, -40px) scale(0.95); }
        }
        @keyframes float-20 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(50px, 30px) scale(1.2); }
        }
        @keyframes float-80 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-30px, -60px) scale(1.1); }
        }
        @keyframes float-50 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(20px, 40px) scale(1.05); }
          66% { transform: translate(-35px, -20px) scale(0.95); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        @keyframes card-enter {
          from { opacity: 0; transform: translateY(30px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        /* Responsive logo sizing */
        .login-logo {
          max-width: 340px;
          width: 90%;
          height: auto;
          position: relative;
          filter: drop-shadow(0 0 20px rgba(0, 150, 255, 0.3));
        }

        .login-logo-glow {
          width: 200px;
          height: 200px;
        }

        .login-card {
          padding: 40px 32px;
        }

        .login-logo-container {
          margin-bottom: 32px;
        }

        .login-form {
          gap: 20px;
        }

        /* Mobile adjustments */
        @media (max-width: 480px) {
          .login-logo {
            max-width: 280px;
            width: 85%;
          }
          .login-logo-glow {
            width: 160px;
            height: 160px;
          }
          .login-card {
            padding: 28px 20px;
          }
          .login-logo-container {
            margin-bottom: 24px;
          }
          .login-form {
            gap: 16px;
          }
        }

        /* Very small screens / short height */
        @media (max-height: 650px) {
          .login-logo {
            max-width: 220px;
            width: 70%;
          }
          .login-logo-glow {
            width: 120px;
            height: 120px;
          }
          .login-card {
            padding: 20px 16px;
          }
          .login-logo-container {
            margin-bottom: 16px;
          }
          .login-form {
            gap: 12px;
          }
        }

        /* Override browser autofill styles */
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px rgba(0, 20, 40, 0.95) inset !important;
          -webkit-text-fill-color: #ffffff !important;
          transition: background-color 5000s ease-in-out 0s;
          caret-color: #ffffff;
        }
      `}</style>

      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(-45deg, #0a192f, #0d2137, #0f2847, #0a192f)',
          backgroundSize: '400% 400%',
          animation: 'gradient-shift 15s ease infinite',
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          padding: '16px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Floating orbs for atmosphere */}
        <FloatingOrb size={300} color="#0066ff" initialX={10} initialY={20} duration={20} />
        <FloatingOrb size={250} color="#00d4ff" initialX={70} initialY={60} duration={25} />
        <FloatingOrb size={200} color="#0088ff" initialX={20} initialY={70} duration={18} />
        <FloatingOrb size={180} color="#00aaff" initialX={80} initialY={15} duration={22} />
        <FloatingOrb size={220} color="#0055dd" initialX={50} initialY={80} duration={28} />

        {/* Glassmorphism card */}
        <div
          className="login-card"
          style={{
            background: 'rgba(0, 20, 40, 0.9)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderRadius: '24px',
            border: '1px solid rgba(0, 150, 255, 0.2)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 0 80px rgba(0, 102, 255, 0.1)',
            width: '100%',
            maxWidth: '380px',
            position: 'relative',
            zIndex: 1,
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          {/* Logo with glow */}
          <div
            className="login-logo-container"
            style={{
              textAlign: 'center',
              position: 'relative',
            }}
          >
            {/* Glow behind logo */}
            <div
              className="login-logo-glow"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'radial-gradient(circle, rgba(0, 150, 255, 0.4) 0%, transparent 70%)',
                borderRadius: '50%',
                animation: 'pulse-glow 3s ease-in-out infinite',
                pointerEvents: 'none',
              }}
            />
            <img
              src="/2020.05.20-GPC_logo-transbg-3508px.png"
              alt="GPC Logo"
              className="login-logo"
            />
          </div>

          {/* Error message */}
          {error && (
            <div
              style={{
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#fca5a5',
                padding: '12px 16px',
                borderRadius: '12px',
                fontSize: '14px',
                marginBottom: '20px',
                textAlign: 'center',
              }}
            >
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={(e) => void handleSubmit(e)} className="login-form" style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Email input */}
            <div>
              <div style={{ position: 'relative' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: emailError ? '#f87171' : emailFocused ? '#00d4ff' : 'rgba(255, 255, 255, 0.5)',
                    transition: 'color 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <EmailIcon />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError('');
                  }}
                  onFocus={() => setEmailFocused(true)}
                  onBlur={() => {
                    setEmailFocused(false);
                    if (email && !isValidEmail(email)) {
                      setEmailError('Please enter a valid email address');
                    }
                  }}
                  placeholder="Email address"
                  autoComplete="email"
                  required
                  style={{
                    width: '100%',
                    padding: '16px 16px 16px 52px',
                    background: emailFocused ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.06)',
                    border: emailError
                      ? '2px solid rgba(248, 113, 113, 0.5)'
                      : emailFocused
                        ? '2px solid rgba(0, 212, 255, 0.5)'
                        : '2px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '14px',
                    fontSize: '16px',
                    color: '#ffffff',
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    boxShadow: emailError
                      ? '0 0 20px rgba(248, 113, 113, 0.15)'
                      : emailFocused
                        ? '0 0 30px rgba(0, 212, 255, 0.2)'
                        : 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              {emailError && (
                <div
                  style={{
                    color: '#f87171',
                    fontSize: '13px',
                    marginTop: '6px',
                    marginLeft: '4px',
                  }}
                >
                  {emailError}
                </div>
              )}
            </div>

            {/* Password input */}
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: passwordFocused ? '#00d4ff' : 'rgba(255, 255, 255, 0.5)',
                  transition: 'color 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <LockIcon />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                placeholder="Password"
                autoComplete="current-password"
                required
                style={{
                  width: '100%',
                  padding: '16px 52px 16px 52px',
                  background: passwordFocused ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.06)',
                  border: passwordFocused ? '2px solid rgba(0, 212, 255, 0.5)' : '2px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '14px',
                  fontSize: '16px',
                  color: '#ffffff',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  boxShadow: passwordFocused ? '0 0 30px rgba(0, 212, 255, 0.2)' : 'none',
                  boxSizing: 'border-box',
                }}
              />
              {/* Password visibility toggle */}
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '4px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  padding: '12px',
                  cursor: 'pointer',
                  color: showPassword ? '#00d4ff' : 'rgba(255, 255, 255, 0.5)',
                  transition: 'color 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#00d4ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = showPassword ? '#00d4ff' : 'rgba(255, 255, 255, 0.5)';
                }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '16px 24px',
                background: loading
                  ? 'rgba(0, 80, 180, 0.5)'
                  : 'linear-gradient(135deg, #0044aa 0%, #0066cc 50%, #0077dd 100%)',
                backgroundSize: '200% 200%',
                color: 'white',
                border: 'none',
                borderRadius: '14px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: loading ? 'none' : '0 10px 40px rgba(0, 70, 150, 0.5)',
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                opacity: loading ? 0.7 : 1,
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 15px 50px rgba(0, 70, 150, 0.6)';
                  e.currentTarget.style.backgroundPosition = '100% 0';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = loading ? 'none' : '0 10px 40px rgba(0, 70, 150, 0.5)';
                e.currentTarget.style.backgroundPosition = '0% 0';
              }}
              onMouseDown={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(1px) scale(0.98)';
                }
              }}
              onMouseUp={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
            >
              {loading ? (
                <>
                  <Spinner />
                  <span>Signing in...</span>
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
